import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { TrendingUp, Users, Clock, AlertTriangle, Shield, RefreshCw, ArrowRight, Bell, Search, ListChecks, Activity, ServerCog, Check, X, UserCheck, XCircle } from "lucide-react";
import api from "@/lib/api";

/**
 * AdminSuperDashboard — MRR / ARR / active-Pro / churn / recent-upgrades
 * feed. Guarded by the same ADMIN_SECRET header as trial extend/lookup —
 * paste-once flow so the secret never touches localStorage.
 */
export default function AdminSuperDashboard() {
  const [secret, setSecret] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!secret) {
      setData(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get("/auth/admin/metrics", {
        headers: { "X-Admin-Secret": secret },
      });
      setData(data);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Failed to load metrics";
      setError(msg);
      setData(null);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [secret]);

  return (
    <div className="max-w-6xl mx-auto">
      <div className="mb-8">
        <div className="text-xs tracking-[0.24em] uppercase text-[var(--text-muted)] mb-2">
          Admin · Super Dashboard
        </div>
        <h1 className="text-3xl font-heading font-medium text-[var(--text-primary)]">
          Revenue & subscriber overview
        </h1>
        <p className="text-sm text-[var(--text-secondary)] mt-2 max-w-xl">
          MRR / ARR / active-Pro count / churn (30 d) plus the last ten
          upgrades and admin extends. Paste the <code>ADMIN_SECRET</code>
          {" "}from backend <code>.env</code> to fetch.
        </p>
      </div>

      <div className="card-flat p-5 mb-6 flex flex-wrap items-center gap-3">
        <Shield size={16} className="text-[var(--text-secondary)]" />
        <input
          type="password"
          className="field-input flex-1 min-w-[240px]"
          placeholder="Paste ADMIN_SECRET"
          value={secret}
          onChange={(e) => setSecret(e.target.value)}
          data-testid="admin-metrics-secret"
        />
        <button
          className="btn-primary"
          onClick={load}
          disabled={loading || !secret}
          data-testid="admin-metrics-load"
        >
          <RefreshCw size={15} className={loading ? "animate-spin" : ""} />
          {loading ? "Loading…" : "Load metrics"}
        </button>
        <Link to="/admin/trials" className="btn-ghost">
          Extend a trial <ArrowRight size={14}/>
        </Link>
        <Link to="/admin/users" className="btn-ghost" data-testid="admin-nav-users">
          <Search size={13}/> User lookup
        </Link>
        <Link to="/admin/waitlist" className="btn-ghost" data-testid="admin-nav-waitlist">
          <ListChecks size={13}/> Waitlist
        </Link>
        <Link to="/admin/audit" className="btn-ghost" data-testid="admin-nav-audit">
          <Activity size={13}/> Audit log
        </Link>
      </div>

      {error && (
        <div className="card-flat p-4 mb-6 border-[var(--accent-danger)] text-sm text-[var(--accent-danger)]" data-testid="admin-metrics-error">
          {error}
        </div>
      )}

      <SystemConfigPanel />

      {data && <MetricsView data={data} secret={secret} />}
    </div>
  );
}

/**
 * SystemConfigPanel — fetches /api/system/health (public, no secret
 * needed) and renders one green/red pill per env-var. Lets an admin
 * verify at a glance which config keys are set on the currently
 * running deploy.
 */
function SystemConfigPanel() {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const { data } = await api.get("/system/health");
      setHealth(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Health check failed");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const cfg = health?.config || {};
  const adminEmails = cfg.admin_emails || {};
  const KEYS = [
    ["admin_emails", "ADMIN_EMAILS", adminEmails.configured],
    ["mongo_url", "MONGO_URL", cfg.mongo_url],
    ["db_name", "DB_NAME", cfg.db_name],
    ["emergent_llm_key", "EMERGENT_LLM_KEY", cfg.emergent_llm_key],
    ["resend_api_key", "RESEND_API_KEY", cfg.resend_api_key],
    ["paytabs_profile_id", "PAYTABS_PROFILE_ID", cfg.paytabs_profile_id],
    ["plaid_client_id", "PLAID_CLIENT_ID", cfg.plaid_client_id],
    ["alphavantage_api_key", "ALPHAVANTAGE_API_KEY", cfg.alphavantage_api_key],
    ["cors_origins", "CORS_ORIGINS", cfg.cors_origins],
  ];

  return (
    <div className="card-flat p-5 mb-6" data-testid="admin-config-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <div>
          <div className="flex items-center gap-2">
            <ServerCog size={15} className="text-[var(--accent-primary)]"/>
            <div className="label-eyebrow">Environment config</div>
          </div>
          <p className="text-sm text-[var(--text-secondary)] mt-2 max-w-lg leading-relaxed">
            One pill per env var. Green = set on the currently running
            deploy. If <code>ADMIN_EMAILS</code> shows red on production,
            add it in the Emergent Deployments panel and redeploy.
          </p>
        </div>
        <button
          className="btn-ghost"
          onClick={load}
          disabled={loading}
          data-testid="admin-config-refresh"
        >
          <RefreshCw size={14} className={loading ? "animate-spin" : ""}/>
          Refresh
        </button>
      </div>

      {err && (
        <div className="text-sm text-[var(--accent-danger)]" data-testid="admin-config-error">
          {err}
        </div>
      )}

      {health && (
        <>
          <div className="flex flex-wrap gap-2 mb-4">
            {KEYS.map(([key, label, ok]) => (
              <span
                key={key}
                data-testid={`config-pill-${key}`}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-mono border ${
                  ok
                    ? "bg-[var(--accent-positive-bg,#E7F5EC)] text-[var(--accent-positive,#2F7A48)] border-[var(--accent-positive,#2F7A48)]/30"
                    : "bg-[var(--accent-danger-bg,#FBEAEA)] text-[var(--accent-danger,#B23A3A)] border-[var(--accent-danger,#B23A3A)]/30"
                }`}
              >
                {ok ? <Check size={12}/> : <X size={12}/>}
                {label}
              </span>
            ))}
          </div>

          {adminEmails.configured && (
            <div className="text-xs text-[var(--text-muted)] font-mono" data-testid="admin-config-fingerprint">
              ADMIN_EMAILS · {adminEmails.count} entry · sha256:{adminEmails.sha256_prefix}
              <span className="ml-2 text-[var(--text-secondary)]">
                (match this against preview to confirm same value)
              </span>
            </div>
          )}

          <div className="text-xs text-[var(--text-muted)] font-mono mt-2">
            {health.service} v{health.version} · env: {health.environment} · snapshot: {health.server_time_utc?.slice(0,19)}Z
          </div>
        </>
      )}
    </div>
  );
}

function MetricsView({ data, secret }) {
  return (
    <>
      <ImpersonationFeed secret={secret} />
      <OnboardingFunnelCard secret={secret} />
      <MetricsViewBody data={data} secret={secret} />
    </>
  );
}

/**
 * OnboardingFunnelCard — 30-day conversion funnel from signup through
 * first meaningful actions. Uses /auth/admin/onboarding-funnel.
 */
function OnboardingFunnelCard({ secret }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [days, setDays] = useState(30);

  const load = useCallback(async () => {
    if (!secret) return;
    setLoading(true); setErr(null);
    try {
      const { data } = await api.get("/auth/admin/onboarding-funnel", {
        headers: { "X-Admin-Secret": secret },
        params: { days },
      });
      setData(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load funnel");
    } finally { setLoading(false); }
  }, [secret, days]);

  useEffect(() => { load(); }, [load]);

  const totals = data?.totals || {};
  const signups = totals.signup || 0;
  const rows = [
    ["signup",                "Signups",                 totals.signup || 0],
    ["empty_cta_seeded",      "Tried sample data",       totals.empty_cta_seeded || 0],
    ["empty_cta_add_expense", "Chose add-my-own",        totals.empty_cta_add_expense || 0],
    ["first_expense",         "First expense added",     totals.first_expense || 0],
    ["first_view_reports",    "First Reports view",      totals.first_view_reports || 0],
    ["first_view_optimizer",  "First Optimizer view",    totals.first_view_optimizer || 0],
    ["first_view_advisor",    "First Advisor view",      totals.first_view_advisor || 0],
  ];
  const maxWidth = Math.max(1, ...rows.map((r) => r[2]));

  return (
    <div className="card-flat p-5 mb-6" data-testid="admin-onboarding-funnel">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <div>
          <div className="flex items-center gap-2">
            <TrendingUp size={15} className="text-[var(--accent-primary)]" />
            <div className="label-eyebrow">Onboarding funnel · last {data?.days || days}d</div>
          </div>
          <p className="text-sm text-[var(--text-secondary)] mt-2 max-w-lg leading-relaxed">
            Conversion from signup through first meaningful actions.
            Higher numbers on the right = users engage deeper before dropping off.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            className="btn-ghost text-xs"
            value={days}
            onChange={(e) => setDays(parseInt(e.target.value, 10))}
            data-testid="funnel-days-select"
          >
            <option value={7}>7 days</option>
            <option value={30}>30 days</option>
            <option value={90}>90 days</option>
          </select>
          <button className="btn-ghost" onClick={load} disabled={loading} data-testid="funnel-refresh">
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
          </button>
        </div>
      </div>

      {err && <div className="text-sm text-[var(--accent-danger)] mb-2">{err}</div>}

      <div className="space-y-2" data-testid="funnel-rows">
        {rows.map(([key, label, count]) => {
          const pctOfSignup = signups > 0 && key !== "signup" ? Math.round((count / signups) * 100) : null;
          const barPct = maxWidth > 0 ? Math.max(2, (count / maxWidth) * 100) : 0;
          return (
            <div key={key} className="flex items-center gap-3" data-testid={`funnel-row-${key}`}>
              <div className="w-40 shrink-0 text-xs text-[var(--text-secondary)] truncate">{label}</div>
              <div className="flex-1 h-6 rounded bg-[var(--bg-accent)] relative overflow-hidden">
                <div
                  className="h-full transition-all"
                  style={{
                    width: `${barPct}%`,
                    background: key === "signup"
                      ? "linear-gradient(90deg, var(--accent-primary), #2D5A40)"
                      : "linear-gradient(90deg, rgba(45,90,64,0.6), rgba(224,158,80,0.5))",
                  }}
                />
              </div>
              <div className="w-24 shrink-0 text-right font-mono text-sm">
                {count}
                {pctOfSignup !== null && (
                  <span className="text-[10px] text-[var(--text-muted)] ml-1">({pctOfSignup}%)</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * ImpersonationFeed — recent admin impersonation events + one-click
 * revoke on active sessions. Keeps admin actions auditable and
 * revocable from a single surface.
 */
function ImpersonationFeed({ secret }) {
  const [events, setEvents] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [busyPrefix, setBusyPrefix] = useState(null);

  const load = useCallback(async () => {
    if (!secret) return;
    setLoading(true); setErr(null);
    try {
      const { data } = await api.get("/auth/admin/impersonate-audit", {
        headers: { "X-Admin-Secret": secret },
      });
      setEvents(data.events || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load impersonation feed");
    } finally { setLoading(false); }
  }, [secret]);

  useEffect(() => { load(); }, [load]);

  const revoke = async (prefix, email) => {
    if (!window.confirm(`Revoke active impersonation of ${email}? The admin using it will be logged out.`)) return;
    setBusyPrefix(prefix);
    try {
      const { data } = await api.post(
        `/auth/admin/impersonate/${prefix}/revoke`,
        null,
        { headers: { "X-Admin-Secret": secret } },
      );
      toast.success(`Revoked ${data.revoked} session${data.revoked === 1 ? "" : "s"}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Revoke failed");
    } finally { setBusyPrefix(null); }
  };

  return (
    <div className="card-flat p-5 mb-6" data-testid="admin-impersonation-feed">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <div>
          <div className="flex items-center gap-2">
            <UserCheck size={15} style={{ color: "#E09E50" }} />
            <div className="label-eyebrow" style={{ color: "#E09E50" }}>Impersonation history</div>
          </div>
          <p className="text-sm text-[var(--text-secondary)] mt-2 max-w-lg leading-relaxed">
            Every &quot;Sign in as user&quot; event. Active sessions can be revoked
            instantly, forcing the impersonating admin to log out.
          </p>
        </div>
        <button className="btn-ghost" onClick={load} disabled={loading} data-testid="impersonation-feed-refresh">
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      {err && <div className="text-sm text-[var(--accent-danger)] mb-2">{err}</div>}
      {events && events.length === 0 && (
        <div className="text-sm text-[var(--text-muted)]">No impersonation events yet.</div>
      )}

      {events && events.length > 0 && (
        <div className="space-y-2" data-testid="impersonation-feed-rows">
          {events.map((ev) => (
            <div
              key={ev.id}
              className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg border border-[var(--border-default)] text-sm"
              data-testid={`impersonation-row-${ev.session_token_prefix}`}
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <strong className="font-medium truncate">{ev.target_email || ev.target_user_id}</strong>
                  {ev.is_active && (
                    <span className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-[var(--accent-success)]/15 text-[var(--accent-success)]" data-testid="impersonation-active-pill">
                      Active
                    </span>
                  )}
                </div>
                <div className="text-[11px] text-[var(--text-muted)] font-mono mt-0.5">
                  {shortDate(ev.at)} · admin {ev.admin_ip} · session:{ev.session_token_prefix}
                </div>
              </div>
              {ev.is_active && (
                <button
                  onClick={() => revoke(ev.session_token_prefix, ev.target_email)}
                  disabled={busyPrefix === ev.session_token_prefix}
                  data-testid={`impersonation-revoke-${ev.session_token_prefix}`}
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium border border-[var(--accent-danger)] text-[var(--accent-danger)] hover:bg-[var(--accent-danger)]/10 transition-colors shrink-0"
                >
                  <XCircle size={12} />
                  {busyPrefix === ev.session_token_prefix ? "…" : "Revoke"}
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function MetricsViewBody({ data, secret }) {
  const rev = data.revenue || {};
  const subs = data.subscribers || {};
  const trials = data.trials || {};
  const churn = data.churn || {};
  const totals = data.totals || {};
  const [alertPreview, setAlertPreview] = useState(null);
  const [alertBusy, setAlertBusy] = useState(false);
  const [funnel, setFunnel] = useState(null);
  const [funnelBusy, setFunnelBusy] = useState(false);

  const dryRunAlerts = async () => {
    setAlertBusy(true);
    try {
      const { data } = await api.post("/auth/admin/alerts/run?dry_run=true", null, {
        headers: { "X-Admin-Secret": secret },
      });
      setAlertPreview(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
    } finally { setAlertBusy(false); }
  };

  const loadFunnel = async () => {
    setFunnelBusy(true);
    try {
      const { data } = await api.get("/analytics/trial-funnel", {
        headers: { "X-Admin-Secret": secret },
      });
      setFunnel(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Funnel load failed");
    } finally { setFunnelBusy(false); }
  };

  useEffect(() => { loadFunnel(); }, []);

  return (
    <>
      {/* Revenue row */}
      <div className="grid md:grid-cols-4 gap-4 mb-6 stagger">
        <MetricCard label="MRR (OMR)" value={fmtOmr(rev.mrr_omr)} icon={<TrendingUp size={18}/>} tone="pos" testId="metric-mrr" />
        <MetricCard label="ARR (OMR)" value={fmtOmr(rev.arr_omr)} icon={<TrendingUp size={18}/>} tone="pos" testId="metric-arr" />
        <MetricCard label="Active Pro" value={String(subs.active_pro ?? 0)} sub={`${subs.plan_monthly || 0} mo · ${subs.plan_annual || 0} yr`} icon={<Users size={18}/>} testId="metric-active-pro" />
        <MetricCard label="Total users" value={String(totals.users ?? 0)} icon={<Users size={18}/>} testId="metric-total-users" />
      </div>

      {/* Trial + Churn row */}
      <div className="grid md:grid-cols-4 gap-4 mb-8 stagger">
        <MetricCard label="Trials active" value={String(trials.active ?? 0)} icon={<Clock size={18}/>} testId="metric-trials-active" />
        <MetricCard label="Trials expiring 7d" value={String(trials.expiring_7d ?? 0)} icon={<Clock size={18}/>} tone="warn" testId="metric-trials-expiring" />
        <MetricCard label="Trials expired" value={String(trials.expired ?? 0)} icon={<AlertTriangle size={18}/>} tone="warn" testId="metric-trials-expired" />
        <MetricCard label="Churn (30d)" value={`${churn.churn_rate_pct ?? 0}%`} sub={`${churn.churn_30d ?? 0} of ${(churn.churn_30d ?? 0) + (churn.upgrades_30d ?? 0)} events`} icon={<AlertTriangle size={18}/>} tone={churn.churn_rate_pct >= 10 ? "neg" : "warn"} testId="metric-churn" />
      </div>

      {/* Trial funnel */}
      <div className="card-flat p-5 mb-6" data-testid="admin-funnel-panel">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
          <div>
            <div className="label-eyebrow">Trial funnel</div>
            <p className="text-sm text-[var(--text-secondary)] mt-2 max-w-lg leading-relaxed">
              Cohort-correlated: for each window, counts trials that
              started inside the window and how many of that same
              cohort now sit on Pro. Activation rate =
              <code> pro_upgraded / trial_started</code> (capped at
              100%).
            </p>
          </div>
          <button
            className="btn-ghost"
            onClick={loadFunnel}
            disabled={funnelBusy}
            data-testid="admin-funnel-refresh"
          >
            <RefreshCw size={14} className={funnelBusy ? "animate-spin" : ""}/>
            Refresh
          </button>
        </div>
        {funnel && (
          <div className="grid md:grid-cols-3 gap-3">
            {["24h", "7d", "30d"].map((w) => {
              const row = funnel.windows?.[w] || {};
              return (
                <div key={w} className="p-4 rounded-xl bg-[var(--bg-accent)]"
                     data-testid={`funnel-window-${w}`}>
                  <div className="text-[10px] uppercase tracking-[0.24em] text-[var(--text-muted)] mb-3">{w}</div>
                  <FunnelRow label="Trial started"     value={row.trial_started}     />
                  <FunnelRow label="Ended early"       value={row.trial_ended_early} />
                  <FunnelRow label="Trial expired"     value={row.trial_expired}     />
                  <FunnelRow label="Pro upgraded"      value={row.pro_upgraded} tone="pos" />
                  <div className="mt-3 pt-3 border-t border-[var(--border-default)] flex items-center justify-between">
                    <span className="text-xs text-[var(--text-secondary)]">Activation</span>
                    <span className="font-mono font-semibold text-[var(--accent-primary)]"
                          data-testid={`funnel-activation-${w}`}>
                      {row.activation_rate_pct ?? 0}%
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Retention-alert dry-run */}
      <div className="card-flat p-5 mb-6" data-testid="admin-alerts-panel">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2">
              <Bell size={15} className="text-[var(--accent-primary)]"/>
              <div className="label-eyebrow">Retention alerts</div>
            </div>
            <p className="text-sm text-[var(--text-secondary)] mt-2 max-w-lg leading-relaxed">
              Automatic Resend email pings the admin inbox when
              <span className="font-mono"> churn ≥ 15%</span> or
              <span className="font-mono"> trials expiring ≥ 5</span>. One
              alert per trigger per 7 days. Preview which triggers
              would fire right now:
            </p>
          </div>
          <button
            className="btn-primary"
            onClick={dryRunAlerts}
            disabled={alertBusy}
            data-testid="admin-alerts-preview-btn"
          >
            <Bell size={14}/> {alertBusy ? "Checking…" : "Preview triggers"}
          </button>
        </div>
        {alertPreview && (
          <div className="mt-4 text-sm" data-testid="admin-alerts-preview-result">
            <div className="grid md:grid-cols-2 gap-3">
              <PreviewRow label="Triggered now" value={
                alertPreview.triggered?.length
                  ? alertPreview.triggered.join(", ")
                  : "none"
              }/>
              <PreviewRow label="Would send (not cooling down)" value={
                alertPreview.would_send?.length
                  ? alertPreview.would_send.join(", ")
                  : "none"
              }/>
              <PreviewRow label="Churn threshold" value={`${alertPreview.thresholds?.churn_pct}%`}/>
              <PreviewRow label="Expiring threshold" value={String(alertPreview.thresholds?.trials_expiring_7d)}/>
            </div>
          </div>
        )}
      </div>

      {/* Recent upgrades */}
      <FeedCard
        title="Recent upgrades"
        testId="admin-recent-upgrades"
        rows={data.recent_upgrades || []}
        empty="No upgrades yet."
        renderRow={(r, i) => (
          <div key={`up-${r.tran_ref || i}`} className="flex items-center justify-between gap-3 p-3 border-b border-[var(--border-default)] last:border-b-0" data-testid={`upgrade-row-${i}`}>
            <div className="min-w-0">
              <div className="text-sm font-medium text-[var(--text-primary)] truncate">
                {r.email || r.user_id || r.tran_ref}
              </div>
              <div className="text-xs text-[var(--text-muted)] font-mono mt-0.5">
                {shortDate(r.at)} · {r.cycle || "monthly"} {r.signed ? "· signed" : ""}
              </div>
            </div>
            <div className="text-right shrink-0">
              <div className="font-mono font-medium">{r.amount ?? "?"} {r.currency || "OMR"}</div>
            </div>
          </div>
        )}
      />

      {/* Recent extends */}
      <FeedCard
        title="Recent admin extends"
        testId="admin-recent-extends"
        rows={data.recent_extends || []}
        empty="No admin extends recorded."
        renderRow={(r, i) => (
          <div key={`ex-${i}`} className="flex items-center justify-between gap-3 p-3 border-b border-[var(--border-default)] last:border-b-0" data-testid={`extend-row-${i}`}>
            <div className="min-w-0">
              <div className="text-sm font-medium text-[var(--text-primary)] truncate">
                {r.email || r.target_user_id}
              </div>
              <div className="text-xs text-[var(--text-muted)] font-mono mt-0.5">
                {shortDate(r.at)} · +{r.days} days
              </div>
            </div>
            <div className="text-right shrink-0 text-xs text-[var(--text-secondary)]">
              until {shortDate(r.new_trial_ends_at)}
            </div>
          </div>
        )}
      />

      <div className="mt-6 text-xs text-[var(--text-muted)] font-mono">
        Snapshot: {shortDate(data.generated_at)}
      </div>
    </>
  );
}

function PreviewRow({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-3 p-2 rounded-md bg-[var(--bg-accent)]">
      <span className="text-xs text-[var(--text-secondary)] uppercase tracking-wider">{label}</span>
      <span className="font-mono text-sm">{value}</span>
    </div>
  );
}

function FunnelRow({ label, value, tone }) {
  const cls = tone === "pos"
    ? "text-[var(--accent-positive)] font-semibold"
    : "text-[var(--text-primary)]";
  return (
    <div className="flex items-center justify-between gap-3 py-1 text-sm">
      <span className="text-[var(--text-secondary)]">{label}</span>
      <span className={`font-mono ${cls}`}>{value ?? 0}</span>
    </div>
  );
}

function MetricCard({ label, value, sub, icon, tone = "neutral", testId }) {
  const toneCls = tone === "pos"
    ? "text-[var(--accent-positive)]"
    : tone === "warn"
      ? "text-[#A16A25]"
      : tone === "neg"
        ? "text-[var(--accent-danger)]"
        : "text-[var(--text-primary)]";
  return (
    <div className="card-surface p-5" data-testid={testId}>
      <div className="flex items-center justify-between">
        <div className="label-eyebrow">{label}</div>
        <div className="text-[var(--text-secondary)]">{icon}</div>
      </div>
      <div className={`num-lg mt-3 ${toneCls}`}>{value}</div>
      {sub && <div className="text-xs text-[var(--text-muted)] mt-1 font-mono">{sub}</div>}
    </div>
  );
}

function FeedCard({ title, rows, renderRow, empty, testId }) {
  return (
    <div className="card-flat p-5 mb-6" data-testid={testId}>
      <div className="label-eyebrow mb-4">{title}</div>
      {rows.length === 0
        ? <div className="text-sm text-[var(--text-muted)]">{empty}</div>
        : <div className="divide-y divide-[var(--border-default)]">{rows.map(renderRow)}</div>}
    </div>
  );
}

function fmtOmr(v) {
  if (v == null) return "0.000";
  return Number(v).toLocaleString(undefined, { minimumFractionDigits: 3, maximumFractionDigits: 3 });
}
function shortDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}
